<template>
  <div class="page">
    <div class="row head" style="justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap;">
      <div>
        <div class="kicker">Order</div>
        <h2 class="title">Order #{{ orderId || "—" }}</h2>

        <div v-if="orderHeaderLine" class="mono" style="opacity:.8;">
          {{ orderHeaderLine }}
        </div>

        <div v-if="order && (order.customerAddress || order.customer_address)" class="mono" style="opacity:.75;margin-top:4px;">
          {{ order.customerAddress || order.customer_address }}
        </div>
      </div>

      <div class="row" style="gap:8px;flex-wrap:wrap;align-items:center;">
        <button class="btn btn--ghost" @click="refresh" :disabled="loading">
          {{ loading ? "Refreshing…" : "Refresh" }}
        </button>

        <button
          v-if="showReviseQuotation"
          class="btn"
          @click="goReviseQuotation"
          :disabled="loading"
          title="Create a new quotation version and send back to BM"
        >
          Revise Quotation
        </button>


        <button class="btn btn--ghost" @click="goBack" :disabled="loading">
          Back
        </button>
      </div>
    </div>

    <div v-if="error" class="err" style="margin-top:10px;">
      {{ error }}
    </div>

    <!-- Stage + Timeline -->
    <div class="card" style="margin-top:12px;">
      <div class="row" style="justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
        <div class="row" style="gap:10px;align-items:center;flex-wrap:wrap;">
          <span class="badge" :class="stageClass(order && (order.stage || order.Stage))">
            {{ stageLabel(order && (order.stage || order.Stage)) }}
          </span>

          <span v-if="(order && (order.createdAt || order.created_at))" class="mono" style="opacity:.75;">
            Created: {{ prettyDT(order.createdAt || order.created_at) }}
          </span>
          <span v-if="(order && (order.updatedAt || order.updated_at))" class="mono" style="opacity:.75;">
            Updated: {{ prettyDT(order.updatedAt || order.updated_at) }}
          </span>
        </div>

        <div class="row" style="gap:8px;flex-wrap:wrap;" v-if="canDownloadDocs">
          <button
            class="btn btn--ghost"
            @click="downloadQuote"
            :disabled="loading || !order || !getOrderQuotationId()"
            title="Download quotation PDF"
          >
            Download Quote
          </button>

          <button
            class="btn btn--ghost"
            @click="downloadSigned"
            :disabled="loading || !order || !getOrderQuotationId()"
            title="Download signed final PDF"
          >
            Download Signed
          </button>
        </div>

      </div>

      <div class="timeline" style="margin-top:12px;">
        <div
          v-for="s in stages"
          :key="s"
          class="step"
          :class="stepClass(s)"
          :title="s"
        >
          <div class="step__dot"></div>
          <div class="step__label">{{ stageShort(s) }}</div>
        </div>
      </div>

      <div v-if="orderSummaryLine" class="mono" style="margin-top:10px;opacity:.82;">
        {{ orderSummaryLine }}
      </div>
    </div>

    <!-- Payments -->
    <div class="card" style="margin-top:12px;">
      <div class="row" style="justify-content:space-between;align-items:center;">
        <div style="font-weight:900;">Payment Milestones</div>
        <div class="mono" style="opacity:.75;">(Finance-controlled)</div>
      </div>

      <div v-if="payments && payments.length" class="pay-grid" style="margin-top:10px;">
        <div v-for="p in payments" :key="p.id || (p.milestone + '_' + p.amount_due)" class="pay-card">
          <div class="row" style="justify-content:space-between;align-items:center;gap:10px;">
            <div style="font-weight:900;">{{ milestoneLabel(p.milestone) }}</div>
            <span class="pill" :class="(p.isReceived || p.is_received) ? 'pill--good' : 'pill--warn'">
              <span class="dot"></span>
              {{ (p.isReceived || p.is_received) ? "Received" : "Pending" }}
            </span>
          </div>

          <div class="mono" style="margin-top:8px;opacity:.85;">
            Amount: <b>{{ (p.amountDue || p.amount_due) ? "₹" + formatINR(p.amountDue || p.amount_due) : "—" }}</b>
          </div>

          <div v-if="(p.receivedAt || p.received_at)" class="mono" style="margin-top:6px;opacity:.75;">
            Received At: {{ prettyDT(p.receivedAt || p.received_at) }}
          </div>

          <div v-if="p.note" class="mono" style="margin-top:6px;opacity:.8;">
            Note: {{ p.note }}
          </div>
        </div>
      </div>

      <div v-else class="mono" style="margin-top:10px;opacity:.8;">
        No milestone records found.
      </div>

      <div class="mono" style="margin-top:10px;opacity:.8;">
        Gate rules:
        <b>50%</b> unlocks <b>Dispatch</b>,
        <b>40%</b> unlocks <b>Install</b>,
        <b>10%</b> closes the order.
      </div>
    </div>

    <!-- Docs: Inquiry (uploaded by Sales BEFORE order placement) -->
    <div class="card" style="margin-top:12px;">
      <div class="row" style="justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
        <div style="font-weight:900;">Inquiry Docs</div>
        <div class="mono" style="opacity:.75;">(Uploaded by Sales before placing the order)</div>
      </div>

      <div v-if="!canDownloadDocs && inquiryDocs && inquiryDocs.length" class="mono" style="opacity:.65;margin-top:8px;">
        Downloads are restricted for your role.
      </div>


      <div v-if="inquiryDocs && inquiryDocs.length" style="margin-top:10px;">
        <div v-for="d in inquiryDocs" :key="docKey(d, 'inq')" class="att-row">
          <div style="min-width:0;">
            <div class="row" style="gap:10px;align-items:center;flex-wrap:wrap;">
              <span class="badge badge--neutral">{{ d.docType || d.doc_type || 'DOC' }}</span>
              <span class="mono" style="opacity:.85;white-space:nowrap;">
                {{ d.fileName || d.file_name || ("Doc " + (d.id || "")) }}
              </span>
              <span class="mono" style="opacity:.7;" v-if="d.createdAt || d.created_at">
                {{ prettyDT(d.createdAt || d.created_at) }}
              </span>
            </div>
            <div v-if="d.note" class="mono" style="opacity:.75;margin-top:4px;word-break:break-word;">
              Note: {{ d.note }}
            </div>
          </div>

          <div class="row" style="gap:8px;flex-wrap:wrap;align-items:center;">
            <button
              v-if="canDownloadDocs"
              class="btn btn--ghost"
              @click="downloadAnyDoc(d, 'inquiry')"
              :disabled="loading"
            >
              Download
            </button>

            <button
              v-if="canDownloadDocs && (d.storageUrl || d.storage_url)"
              class="btn btn--ghost"
              @click="openStorage(d.storageUrl || d.storage_url)"
              :disabled="loading"
              title="Open storage URL"
            >
              Open
            </button>
          </div>
        </div>
      </div>

      <div v-else class="mono" style="margin-top:10px;opacity:.8;">
        No inquiry docs found.
      </div>
    </div>

    <!-- Docs: Quotations (uploaded by Sales BEFORE order placement) -->
    <div class="card" style="margin-top:12px;">
      <div class="row" style="justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
        <div style="font-weight:900;">Quotation Files</div>
        <div class="mono" style="opacity:.75;">(All versions + signed final)</div>
      </div>

      <div v-if="!canDownloadDocs && inquiryDocs && inquiryDocs.length" class="mono" style="opacity:.65;margin-top:8px;">
        Downloads are restricted for your role.
      </div>


      <div v-if="quotationFiles && quotationFiles.length" style="margin-top:10px;">
        <div v-for="qf in quotationFiles" :key="docKey(qf, 'qf')" class="att-row">
          <div style="min-width:0;">
            <div class="row" style="gap:10px;align-items:center;flex-wrap:wrap;">
              <span
                class="badge"
                :class="(qf.fileKind || qf.file_kind) === 'SIGNED_FINAL' ? 'badge--good' : 'badge--neutral'"
              >
                {{ qf.fileKind || qf.file_kind || 'FILE' }}
              </span>

              <span class="mono" style="opacity:.85;white-space:nowrap;">
                {{ qf.fileName || qf.file_name || ("File " + (qf.id || "")) }}
              </span>

              <span class="mono" style="opacity:.7;" v-if="qf.createdAt || qf.created_at">
                {{ prettyDT(qf.createdAt || qf.created_at) }}
              </span>
            </div>

            <div v-if="qf.note" class="mono" style="opacity:.75;margin-top:4px;word-break:break-word;">
              Note: {{ qf.note }}
            </div>
          </div>

          <div class="row" style="gap:8px;flex-wrap:wrap;align-items:center;">
            <button
              v-if="canDownloadDocs"
              class="btn btn--ghost"
              @click="downloadAnyDoc(qf, 'quotation')"
              :disabled="loading"
            >
              Download
            </button>

            <button
              v-if="canDownloadDocs && (d.storageUrl || d.storage_url)"
              class="btn btn--ghost"
              @click="openStorage(qf.storageUrl || qf.storage_url)"
              :disabled="loading"
              title="Open storage URL"
            >
              Open
            </button>
          </div>
        </div>
      </div>

      <div v-else class="mono" style="margin-top:10px;opacity:.8;">
        No quotation files found.
      </div>
    </div>

    <!-- Docs: Order (uploaded by Dispatch/Installer AFTER order placement) -->
    <div class="card" style="margin-top:12px;">
      <div class="row" style="justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
        <div style="font-weight:900;">Order Docs</div>
        <div class="mono" style="opacity:.75;">(Uploaded by Dispatch / Installer after order)</div>
      </div>

      <div v-if="!canDownloadDocs && inquiryDocs && inquiryDocs.length" class="mono" style="opacity:.65;margin-top:8px;">
        Downloads are restricted for your role.
      </div>


      <div v-if="orderDocs && orderDocs.length" style="margin-top:10px;">
        <div v-for="od in orderDocs" :key="docKey(od, 'od')" class="att-row">
          <div style="min-width:0;">
            <div class="row" style="gap:10px;align-items:center;flex-wrap:wrap;">
              <span class="badge badge--info">{{ od.portal || 'PORTAL' }}</span>
              <span class="badge badge--neutral">{{ od.docType || od.doc_type || 'DOC' }}</span>

              <span class="mono" style="opacity:.85;white-space:nowrap;">
                {{ od.fileName || od.file_name || ("Doc " + (od.id || "")) }}
              </span>

              <span class="mono" style="opacity:.7;" v-if="od.createdAt || od.created_at">
                {{ prettyDT(od.createdAt || od.created_at) }}
              </span>
            </div>

            <div v-if="od.note" class="mono" style="opacity:.75;margin-top:4px;word-break:break-word;">
              Note: {{ od.note }}
            </div>
          </div>

          <div class="row" style="gap:8px;flex-wrap:wrap;align-items:center;">
            <button
              v-if="canDownloadDocs"
              class="btn btn--ghost"
              @click="downloadAnyDoc(od, 'order')"
              :disabled="loading"
            >
              Download
            </button>

            <button
              v-if="canDownloadDocs && (d.storageUrl || d.storage_url)"
              class="btn btn--ghost"
              @click="openStorage(od.storageUrl || od.storage_url)"
              :disabled="loading"
              title="Open storage URL"
            >
              Open
            </button>
          </div>
        </div>
      </div>

      <div v-else class="mono" style="margin-top:10px;opacity:.8;">
        No dispatch/installer docs found.
      </div>
    </div>

    <!-- Events -->
    <div class="card" style="margin-top:12px;">
      <div style="font-weight:900;">Order Activity</div>

      <div v-if="events && events.length" style="margin-top:10px;">
        <div v-for="e in events" :key="e.id || (String(e.eventType || e.event_type) + '_' + String(e.createdAt || e.created_at))" class="evt">
          <div class="row" style="justify-content:space-between;gap:10px;flex-wrap:wrap;">
            <div class="row" style="gap:10px;align-items:center;flex-wrap:wrap;">
              <span class="badge" :class="eventBadgeClass(e.eventType || e.event_type)">
                {{ e.eventType || e.event_type }}
              </span>
              <span class="mono" style="opacity:.85;">
                <span v-if="(e.fromStage || e.from_stage) || (e.toStage || e.to_stage)">
                  {{ e.fromStage || e.from_stage || "—" }} → {{ e.toStage || e.to_stage || "—" }}
                </span>
              </span>
            </div>

            <div class="mono" style="opacity:.7;">
              {{ (e.createdAt || e.created_at) ? prettyDT(e.createdAt || e.created_at) : "—" }}
            </div>
          </div>

          <div v-if="e.note" class="mono" style="opacity:.85;margin-top:6px;word-break:break-word;">
            {{ e.note }}
          </div>
        </div>
      </div>

      <div v-else class="mono" style="margin-top:10px;opacity:.8;">
        No events yet.
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SalesOrderDetail",
  data () {
    return {
      loading: false,
      error: "",

      orderId: null,

      order: null,
      payments: [],
      events: [],

      inquiryDocs: [],
      quotationFiles: [],
      orderDocs: [],

      // FINAL schema stages
      stages: [
        "BM_PENDING",
        "BM_REJECTED",
        "BM_APPROVED",
        "DISPATCH_PENDING",
        "INSTALL_PENDING",
        "CLOSED",
        "CANCELLED"
      ]
    }
  },
  computed: {
    showReviseQuotation () {
      if (!this.order) return false
      var st = this.order.stage || this.order.Stage
      return st === "BM_REJECTED"
    },

    orderHeaderLine () {
      if (!this.order) return ""
      var leadCode = this.order.leadCode || this.order.lead_code
      var inquiryId = this.order.inquiryId || this.order.inquiry_id
      var customerName = this.order.customerName || this.order.customer_name
      var customerPhone = this.order.customerPhone || this.order.customer_phone

      var parts = []
      if (leadCode) parts.push("Lead: " + leadCode)
      if (inquiryId) parts.push("Inquiry: " + inquiryId)
      if (customerName) parts.push(customerName)
      if (customerPhone) parts.push(customerPhone)
      return parts.join(" · ")
    },

    user () {
      return this.$store.getters['auth/currentUser'] || {}
    },
    role () {
      return String((this.user && this.user.role) ? this.user.role : '').toLowerCase()
    },
    canDownloadDocs () {
      return ['sales_employee', 'branch_manager', 'branch_admin', 'super_admin'].includes(this.role)
    },

    orderSummaryLine () {
      if (!this.order) return ""
      var actual = (this.order.actualAmount != null) ? this.order.actualAmount : this.order.actual_amount
      var finalA = (this.order.finalAmount != null) ? this.order.finalAmount : this.order.final_amount
      var disc   = (this.order.discountPct != null) ? this.order.discountPct : this.order.discount_pct


      var bits = []
      if (actual) bits.push("Actual Amount: ₹" + this.formatINR(actual))
      if (finalA) bits.push("Final Amount: ₹" + this.formatINR(finalA))
      if (disc !== undefined && disc !== null && disc !== "") bits.push("Discount: " + disc + "%")
      return bits.join(" · ")
    },

  },
  created () {
    this.resolveOrderId()
    this.refresh()
  },
  methods: {
    resolveOrderId () {
      var id = null
      if (this.$route && this.$route.params && this.$route.params.id) {
        id = this.$route.params.id
      } else if (this.$route && this.$route.query) {
        id = this.$route.query.id || this.$route.query.open
      }
      if (id) {
        this.orderId = id ? String(id) : null
      }
    },

    getOrderInquiryId () {
      if (!this.order) return null
      return this.order.inquiryId || this.order.inquiry_id || null
    },

    getOrderQuotationId () {
      if (!this.order) return null
      return this.order.quotationId || this.order.quotation_id || null
    },

    prettyDT (s) {
      if (!s) return "—"
      try {
        var d = new Date(s)
        if (isNaN(d.getTime())) return String(s)
        return d.toLocaleString()
      } catch (e) {
        return String(s)
      }
    },

    formatINR (n) {
      try {
        var x = parseInt(n)
        if (isNaN(x)) return String(n)
        return x.toLocaleString("en-IN")
      } catch (e) {
        return String(n)
      }
    },

    stageShort (s) {
      if (!s) return "—"
      return String(s).replace(/_/g, " ")
    },

    stageLabel (s) {
      if (!s) return "—"
      var m = {
        "BM_PENDING": "BM Approval Pending",
        "BM_REJECTED": "BM Rejected",
        "BM_APPROVED": "BM Approved",
        "DISPATCH_PENDING": "Dispatch Pending",
        "INSTALL_PENDING": "Install Pending",
        "CLOSED": "Closed",
        "CANCELLED": "Cancelled"
      }
      return m[s] || this.stageShort(s)
    },

    stageIndex (s) {
      if (!s) return -1
      return this.stages.indexOf(s)
    },

    stageClass (s) {
      if (!s) return "badge--neutral"
      if (s === "CLOSED") return "badge--good"
      if (s === "CANCELLED") return "badge--bad"
      if (s === "BM_REJECTED") return "badge--bad"
      if (s === "BM_PENDING") return "badge--warn"
      if (s === "BM_APPROVED") return "badge--info"
      if (s === "DISPATCH_PENDING" || s === "INSTALL_PENDING") return "badge--neutral"
      return "badge--neutral"
    },

    stepClass (s) {
      if (!this.order) return ""
      var curStage = this.order.stage || this.order.Stage
      if (!curStage) return ""

      var cur = this.stageIndex(curStage)
      var i = this.stageIndex(s)
      if (i < 0) return ""

      if (curStage === "BM_REJECTED") {
        if (s === "BM_REJECTED") return "step--current step--bad"
      }
      if (curStage === "CANCELLED") {
        if (s === "CANCELLED") return "step--current step--bad"
      }

      if (i < cur) return "step--done"
      if (i === cur) return "step--current"
      return "step--todo"
    },

    milestoneLabel (m) {
      if (!m) return "—"
      if (m === "ADVANCE_50") return "Advance (50%)"
      if (m === "SECOND_40") return "Second (40%)"
      if (m === "FINAL_10") return "Final (10%)"
      return m
    },

    eventBadgeClass (t) {
      t = (t || "").toUpperCase()
      if (t === "STAGE_CHANGE") return "badge--info"
      if (t === "PAYMENT") return "badge--good"
      if (t === "BM_DECISION") return "badge--warn"
      if (t === "DOC_UPLOAD") return "badge--neutral"
      return "badge--neutral"
    },

    pickErr (e, fallback) {
      try {
        if (e && e.response && e.response.data) {
          var d = e.response.data
          if (d.error) return d.error
          if (d.errors && d.errors.length) return d.errors.join(", ")
        }
        if (e && e.message) return e.message
      } catch (x) {}
      return fallback || "failed"
    },

    docKey (d, prefix) {
      var id = d && (d.id || d.storage_url || d.storageUrl || d.file_name || d.fileName || "")
      return prefix + "_" + String(id)
    },

    async refresh () {
      if (!this.orderId) {
        this.error = "Missing order id."
        return
      }
      this.error = ""
      this.loading = true

      try {
        // Primary: load order bundle
        var res = await this.$api.get("/orders/" + this.orderId)
        var data = (res && res.data) ? res.data : {}

        // tolerate bundle shapes: {order,payments,events, ...} OR direct
        this.order = data.order || data || null
        this.payments = Array.isArray(data.payments) ? data.payments : (Array.isArray(this.order && this.order.payments) ? this.order.payments : [])
        this.events = Array.isArray(data.events) ? data.events : (Array.isArray(this.order && this.order.events) ? this.order.events : [])

        // Docs may or may not be included; fetch explicitly (safe)
        await this.fetchAllDocs()
      } catch (e) {
        this.error = this.pickErr(e, "Failed to load order")
      } finally {
        this.loading = false
      }
    },

    async fetchAllDocs () {
      // This function is intentionally defensive: it tries common endpoints.
      var inqId = this.getOrderInquiryId()
      var qId = this.getOrderQuotationId()
      var orderId = this.orderId

      // Reset before refill (prevents stale view)
      this.inquiryDocs = []
      this.quotationFiles = []
      this.orderDocs = []

      // 1) Inquiry docs
      if (inqId) {
        var gotInq = await this.tryGetFirst([
          "/inquiries/" + inqId + "/attachments"
        ])

        if (gotInq && Array.isArray(gotInq)) this.inquiryDocs = gotInq
        if (gotInq && gotInq.docs && Array.isArray(gotInq.docs)) this.inquiryDocs = gotInq.docs
      }

      // 2) Quotation files
      if (qId) {
        var gotQf = await this.tryGetFirst([
          "/quotations/" + qId + "/files",
          "/quotation/" + qId + "/files"
        ])
        if (gotQf && Array.isArray(gotQf)) this.quotationFiles = gotQf
        if (gotQf && gotQf.files && Array.isArray(gotQf.files)) this.quotationFiles = gotQf.files
      }

      // 3) Order docs (dispatch/installer)
      if (orderId) {
        var gotOd = await this.tryGetFirst([
          "/orders/" + orderId + "/docs",
          "/orders/" + orderId + "/documents"
        ])
        if (gotOd && Array.isArray(gotOd)) this.orderDocs = gotOd
        if (gotOd && gotOd.docs && Array.isArray(gotOd.docs)) this.orderDocs = gotOd.docs
      }

      // 4) Events (if not provided)
      if (!this.events || !this.events.length) {
        var gotEv = await this.tryGetFirst([
          "/orders/" + orderId + "/events",
          "/order/" + orderId + "/events"
        ])
        if (gotEv && Array.isArray(gotEv)) this.events = gotEv
        if (gotEv && gotEv.events && Array.isArray(gotEv.events)) this.events = gotEv.events
      }
    },

    async tryGetFirst (paths) {
      for (var i = 0; i < paths.length; i++) {
        try {
          var r = await this.$api.get(paths[i])
          return r && r.data ? r.data : null
        } catch (e) {
          // keep trying
        }
      }
      return null
    },

    goBack () {
      if (this.$router) this.$router.back()
    },

    goReviseQuotation () {
      if (!this.order) return
      var inqId = this.getOrderInquiryId()
      if (!inqId) return

      // We need leadId so "View Inquiry Attachments" works in SalesQuotations too.
      // If order payload doesn’t include leadId, we still pass inquiry+order.
      var leadId = this.order.leadId || this.order.lead_id || null

      this.$router.push({
        name: "sales-quotations",
        query: {
          inquiryId: String(inqId),
          orderId: String(this.orderId),
          ...(leadId ? { leadId: String(leadId) } : {})
        }
      })
    },


    async downloadQuote () {

      if (!this.canDownloadDocs) {
        this.error = "You don’t have permission to download documents."
        return
      }
      var qid = this.getOrderQuotationId()
      if (!qid) return

      var fid = await this.getQuotationFileIdByKind(qid, "QUOTE_PDF")
      if (!fid) {
        this.error = "Quote PDF not found on server."
        return
      }

      await this.downloadAuthed("/quotations/files/" + fid + "/download", "quote_" + qid + ".pdf")
    },

    async downloadSigned () {

      if (!this.canDownloadDocs) {
        this.error = "You don’t have permission to download documents."
        return
      }
      var qid = this.getOrderQuotationId()
      if (!qid) return

      var fid = await this.getQuotationFileIdByKind(qid, "SIGNED_FINAL")
      if (!fid) {
        this.error = "Signed final PDF not found on server."
        return
      }

      await this.downloadAuthed("/quotations/files/" + fid + "/download", "signed_" + qid + ".pdf")
    },

    async getQuotationFileIdByKind (quotationId, kind) {
      try {
        // Option A: reuse already fetched quotationFiles if present
        if (this.quotationFiles && this.quotationFiles.length) {
          for (var i = 0; i < this.quotationFiles.length; i++) {
            var k = this.quotationFiles[i].fileKind || this.quotationFiles[i].file_kind
            if (k === kind) return this.quotationFiles[i].id
          }
        }

        // Option B: fetch fresh (most reliable)
        var res = await this.$api.get("/quotations/" + quotationId + "/files")
        var arr = Array.isArray(res.data) ? res.data : []
        for (var j = 0; j < arr.length; j++) {
          if ((arr[j].fileKind || "") === kind) return arr[j].id
        }
      } catch (e) {}
      return null
    },


    findQuotationFile (kind) {
      if (!this.quotationFiles || !this.quotationFiles.length) return null
      for (var i = 0; i < this.quotationFiles.length; i++) {
        var k = this.quotationFiles[i].fileKind || this.quotationFiles[i].file_kind
        if (k === kind) return this.quotationFiles[i]
      }
      return null
    },

    openStorage (url) {
      try {
        window.open(url, "_blank")
      } catch (e) {}
    },

    async downloadAnyDoc (doc, scope) {

      if (!this.canDownloadDocs) {
        this.error = "You don’t have permission to download documents."
        return
      }
      // If backend provides storage_url, we can open it.
      var storage = doc && (doc.storageUrl || doc.storage_url)
      if (storage) {
        this.openStorage(storage)
        return
      }

      // Otherwise try likely download endpoints (best-effort).
      var id = doc && doc.id
      if (!id) return

      var filename = (doc.fileName || doc.file_name || ("file_" + id))

      var paths = []
      if (scope === "inquiry") {
        paths = [
          "/inquiries/attachments/" + id + "/download"
        ]

      } else if (scope === "quotation") {
        paths = [
          "/quotation-files/" + id + "/download",
          "/quotations/files/" + id + "/download"
        ]
      } else {
        // order
        paths = [
          "/order-docs/" + id + "/download",
          "/orders/docs/" + id + "/download"
        ]
      }

      await this.downloadFromFirst(paths, filename)
    },

    async downloadFromFirst (paths, filename) {
      for (var i = 0; i < paths.length; i++) {
        try {
          await this.downloadAuthed(paths[i], filename)
          return true
        } catch (e) {
          // try next
        }
      }
      return false
    },

    async downloadAuthed (path, filename) {
      // throws on failure so downloadFromFirst can try next
      var res = await this.$api.get(path, { responseType: "blob" })
      var blob = res.data
      var url = window.URL.createObjectURL(blob)

      var link = document.createElement("a")
      link.href = url
      link.download = filename || "file"
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.page{ padding:14px; }

.head .kicker{
  font-weight:900;
  font-size:12px;
  letter-spacing:.06em;
  text-transform:uppercase;
  opacity:.7;
}
.title{ margin:2px 0 0 0; font-weight:1000; }

.card{
  background:#fff;
  border:1px solid rgba(0,0,0,.08);
  border-radius:16px;
  padding:14px;
  box-shadow: 0 10px 30px rgba(0,0,0,.04);
}

.row{ display:flex; }

.inp{
  border:1px solid rgba(0,0,0,.14);
  border-radius:10px;
  padding:8px 10px;
  outline:none;
  background:#fff;
}

.btn{
  border:1px solid rgba(0,0,0,.12);
  background:#111827;
  color:#fff;
  padding:9px 12px;
  border-radius:12px;
  font-weight:900;
  cursor:pointer;
}
.btn:disabled{ opacity:.55; cursor:not-allowed; }
.btn--ghost{ background:#fff; color:#111827; }
.btn--danger{ background:#b91c1c; color:#fff; }

.mono{
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.err{
  background: rgba(185, 28, 28, .08);
  border: 1px solid rgba(185, 28, 28, .18);
  color:#991b1b;
  padding:10px 12px;
  border-radius:12px;
  font-weight:800;
}

/* badges */
.badge{
  display:inline-flex;
  align-items:center;
  padding:6px 10px;
  border-radius:999px;
  border:1px solid rgba(0,0,0,.10);
  font-weight:1000;
  font-size:12px;
}
.badge--good{ background: rgba(16,185,129,.10); border-color: rgba(16,185,129,.25); color:#065f46; }
.badge--info{ background: rgba(59,130,246,.10); border-color: rgba(59,130,246,.25); color:#1e3a8a; }
.badge--warn{ background: rgba(245,158,11,.10); border-color: rgba(245,158,11,.25); color:#92400e; }
.badge--bad{  background: rgba(239,68,68,.10); border-color: rgba(239,68,68,.22); color:#7f1d1d; }
.badge--neutral{ background: rgba(156,163,175,.10); border-color: rgba(156,163,175,.25); color:#374151; }

/* pills */
.pill{
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding:7px 10px;
  border-radius:999px;
  border:1px solid rgba(0,0,0,.10);
  background:#fff;
  font-weight:900;
  font-size:12px;
}
.pill .dot{ width:10px;height:10px;border-radius:99px;background:#9ca3af; }
.pill--good{ border-color: rgba(16,185,129,.25); background: rgba(16,185,129,.08); color:#065f46; }
.pill--good .dot{ background:#10b981; }
.pill--warn{ border-color: rgba(245,158,11,.25); background: rgba(245,158,11,.08); color:#92400e; }
.pill--warn .dot{ background:#f59e0b; }

/* timeline */
.timeline{
  display:flex;
  gap:8px;
  overflow:auto;
  padding-bottom:6px;
}
.step{
  min-width: 140px;
  padding:10px;
  border-radius:14px;
  border:1px solid rgba(0,0,0,.08);
  background:#fff;
  display:flex;
  align-items:center;
  gap:10px;
}
.step__dot{
  width:12px;height:12px;border-radius:99px;
  background:#d1d5db;
  flex:0 0 auto;
}
.step__label{
  font-weight:900;
  font-size:12px;
  opacity:.85;
  white-space:nowrap;
}
.step--done{
  background: rgba(16,185,129,.08);
  border-color: rgba(16,185,129,.22);
}
.step--done .step__dot{ background:#10b981; }
.step--current{
  background: rgba(59,130,246,.10);
  border-color: rgba(59,130,246,.22);
}
.step--current .step__dot{ background:#3b82f6; }
.step--todo{ opacity:.75; }
.step--bad{
  background: rgba(239,68,68,.10);
  border-color: rgba(239,68,68,.22);
}
.step--bad .step__dot{ background:#ef4444; }

/* payments */
.pay-grid{
  display:grid;
  grid-template-columns: repeat(3, minmax(0,1fr));
  gap:10px;
}
@media (max-width: 980px){
  .pay-grid{ grid-template-columns: 1fr; }
}
.pay-card{
  border:1px solid rgba(0,0,0,.08);
  border-radius:14px;
  padding:12px;
  background:#fff;
}

/* doc rows */
.att-row{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:10px;
  padding:10px 0;
  border-top:1px solid rgba(0,0,0,.06);
}
.att-row:first-child{ border-top:none; padding-top:0; }

/* events */
.evt{
  padding:10px 0;
  border-top:1px solid rgba(0,0,0,.06);
}
.evt:first-child{ border-top:none; padding-top:0; }
</style>
